import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, ArrowRight, Mail, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useDeployment } from "@/lib/DeploymentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryOptions = [
  { value: "briefing", label: "Schedule a Municipal Briefing" },
  { value: "partnership", label: "Community Partner / MOU" },
  { value: "funding", label: "Funding & CDBG Discussion" },
  { value: "press", label: "Press / Media" },
  { value: "other", label: "Other" },
];

export default function ContactForm() {
  const d = useDeployment();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    role: "",
    inquiry_type: "briefing",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: typeof e === "string" ? e : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      };
      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      toast.success("Briefing request received. George will follow up by email.");
      setForm({
        full_name: "",
        email: "",
        organization: "",
        role: "",
        inquiry_type: "briefing",
        message: "",
      });
      setDate(null);
    } catch (err) {
      toast.error("Unable to send right now. Please email george.broughton@sterilemate.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="bg-white py-24 lg:py-32 border-t border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                09 — Begin the Conversation
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Request a 30-minute briefing.
            </h2>
            <p className="mt-6 text-[#334155] leading-relaxed">
              George will personally respond within two business days to schedule a
              {" "}{d.contactWalkthroughLine}
              Connector blueprint, and the CDBG partnership framework — tailored to
              your office.
            </p>

            <div className="mt-10 border-t border-[#CBD5E1] pt-8 space-y-5">
              <div className="flex items-start gap-4">
                <Mail className="h-5 w-5 text-[#8B0000] mt-1 shrink-0" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                    Direct
                  </div>
                  <a
                    href="mailto:george.broughton@sterilemate.com"
                    data-testid="contact-email-link"
                    className="font-display text-lg text-[#0A192F] hover:text-[#8B0000] transition-colors"
                  >
                    george.broughton@sterilemate.com
                  </a>
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">
                  Founder
                </div>
                <div className="font-display text-lg text-[#0A192F]">
                  George Broughton
                </div>
                <div className="text-sm text-[#475569]">
                  Program Creator &amp; Founding Architect · CEO, Sterile Mate LLC<br />
                  Service Connector · The Woodlawn Organization / WCDC, Chicago · 2001–2007
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="bg-[#0A192F] text-[#F9F7F1] p-10 lg:p-12 min-h-[500px] flex flex-col justify-center"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-4">
                  Received
                </div>
                <h3 className="font-display text-3xl lg:text-4xl leading-tight">
                  Thank you. Your briefing request is in.
                </h3>
                <p className="mt-4 text-[#CBD5E1] leading-relaxed max-w-md">
                  George will respond personally from
                  <span className="text-[#F9F7F1]"> george.broughton@sterilemate.com </span>
                  within two business days to confirm a time.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  data-testid="contact-send-another"
                  className="mt-8 w-fit rounded-none border-[#F9F7F1] bg-transparent text-[#F9F7F1] hover:bg-[#F9F7F1] hover:text-[#0A192F] h-11 font-mono text-xs uppercase tracking-widest"
                >
                  Send another request
                </Button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                className="bg-[#F9F7F1] border border-[#CBD5E1] p-7 lg:p-10 space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <FieldLabel label="Full Name" required>
                    <Input
                      data-testid="contact-input-name"
                      value={form.full_name}
                      onChange={update("full_name")}
                      placeholder="Maria Hernandez"
                      className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                    />
                  </FieldLabel>
                  <FieldLabel label="Email" required>
                    <Input
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder={d.contactEmailPlaceholder}
                      className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                    />
                  </FieldLabel>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <FieldLabel label="Organization">
                    <Input
                      data-testid="contact-input-org"
                      value={form.organization}
                      onChange={update("organization")}
                      placeholder={d.contactOrgPlaceholder}
                      className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                    />
                  </FieldLabel>
                  <FieldLabel label="Role / Title">
                    <Input
                      data-testid="contact-input-role"
                      value={form.role}
                      onChange={update("role")}
                      placeholder="Director of Community Development"
                      className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
                    />
                  </FieldLabel>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <FieldLabel label="Inquiry Type">
                    <Select value={form.inquiry_type} onValueChange={update("inquiry_type")}>
                      <SelectTrigger
                        data-testid="contact-select-inquiry"
                        className="rounded-none border-[#CBD5E1] bg-white h-11 focus:ring-0 focus:border-[#0A192F]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {inquiryOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value} className="rounded-none">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldLabel>

                  <FieldLabel label="Preferred Date (optional)">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="contact-date-trigger"
                          className="w-full rounded-none border-[#CBD5E1] bg-white h-11 justify-start font-normal text-[#0A192F] hover:bg-white"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#475569]" />
                          {date ? format(date, "PPP") : <span className="text-[#94A3B8]">Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-none" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </FieldLabel>
                </div>

                <FieldLabel label="Message" required>
                  <Textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={6}
                    placeholder="Brief context: your role, why you'd like to meet, and a few times that work."
                    className="rounded-none border-[#CBD5E1] bg-white focus-visible:ring-0 focus-visible:border-[#0A192F]"
                  />
                </FieldLabel>

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="rounded-none w-full bg-[#0A192F] hover:bg-[#15243F] text-[#F9F7F1] h-12 font-mono text-xs uppercase tracking-widest"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send Request <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <p className="text-[10px] font-mono uppercase tracking-widest text-[#64748B]">
                  Confidential. Routed directly to george.broughton@sterilemate.com.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldLabel({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
        {label}
        {required && <span className="text-[#8B0000] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
